import createClient, {} from 'openapi-fetch';
/** Host-only cookie set by the API at sign-in; readable by the SPA, echoed as a header. */
export const CSRF_COOKIE = '__Host-mrigl_csrf';
export const CSRF_HEADER = 'X-CSRF-Token';
/** Error carrying the API problem `code` (UIs localize from it) and Retry-After if any. */
export class ApiError extends Error {
    problem;
    retryAfterSeconds;
    constructor(problem, retryAfterSeconds) {
        super(problem.title);
        this.problem = problem;
        this.retryAfterSeconds = retryAfterSeconds;
        this.name = 'ApiError';
    }
    get code() {
        return this.problem.code;
    }
}
/** Network failure (offline, DNS, CORS): no HTTP response. */
export class NetworkError extends Error {
    constructor(cause) {
        super('network error', { cause });
        this.name = 'NetworkError';
    }
}
export function readCookie(cookieHeader, name) {
    for (const part of cookieHeader.split(';')) {
        const [rawName, ...rest] = part.trim().split('=');
        if (rawName === name)
            return decodeURIComponent(rest.join('='));
    }
    return undefined;
}
const UNSAFE = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
/** Typed client for the admin API. Adds the CSRF header to unsafe requests. */
export function createAdminApi(options = {}) {
    const cookies = options.cookies ?? (() => globalThis.document.cookie);
    const client = createClient({
        // Same origin by default (the admin SPA and admin API share the admin host).
        baseUrl: options.baseUrl ?? (typeof location === 'undefined' ? '' : location.origin),
        credentials: 'same-origin',
        // Resolve fetch at call time (not at import) so instrumentation/polyfills apply.
        fetch: options.fetch ?? ((request) => globalThis.fetch(request)),
    });
    const csrf = {
        onRequest({ request }) {
            if (UNSAFE.has(request.method)) {
                const token = readCookie(cookies(), CSRF_COOKIE);
                if (token !== undefined)
                    request.headers.set(CSRF_HEADER, token);
            }
            return request;
        },
    };
    client.use(csrf);
    return client;
}
function isProblem(value) {
    return (typeof value === 'object' &&
        value !== null &&
        typeof value.code === 'string' &&
        typeof value.status === 'number');
}
/**
 * Turns an openapi-fetch result into data or a thrown ApiError. Usage:
 * `const me = await unwrap(api.GET('/admin-api/v1/me'))`.
 */
export async function unwrap(pending) {
    let result;
    try {
        result = await pending;
    }
    catch (cause) {
        throw new NetworkError(cause);
    }
    if (result.response.ok)
        return result.data;
    const retryAfter = Number(result.response.headers.get('retry-after'));
    const problem = isProblem(result.error)
        ? result.error
        : {
            type: 'about:blank',
            title: result.response.statusText,
            status: result.response.status,
            code: 'unknown',
        };
    throw new ApiError(problem, Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined);
}
//# sourceMappingURL=index.js.map